import type * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

function Table({ className, ...props }: React.ComponentProps<'table'>) {
    return (
        <div data-slot="table-container" className="relative w-full overflow-x-auto">
            <table
                data-slot="table"
                className={cn('w-full caption-bottom text-sm', className)}
                {...props}
            />
        </div>
    );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
    return (
        <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />
    );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
    return (
        <tbody
            data-slot="table-body"
            className={cn('[&_tr:last-child]:border-0', className)}
            {...props}
        />
    );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
    return (
        <tfoot
            data-slot="table-footer"
            className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
            {...props}
        />
    );
}

const tableRowVariants = cva('border-b transition-colors', {
    variants: {
        variant: {
            default: 'hover:bg-muted/50 data-[state=selected]:bg-muted',
            detail: 'bg-background',
        },
    },
    defaultVariants: { variant: 'default' },
});

function TableRow({
    className,
    variant,
    ...props
}: React.ComponentProps<'tr'> & VariantProps<typeof tableRowVariants>) {
    return (
        <tr
            data-slot="table-row"
            className={cn(tableRowVariants({ variant }), className)}
            {...props}
        />
    );
}

const tableHeadVariants = cva(
    'h-10 px-2 text-left align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
    {
        variants: {
            variant: {
                default: 'text-foreground bg-muted font-medium',
                plain: 'bg-transparent text-muted-foreground font-bold',
            },
        },
        defaultVariants: { variant: 'default' },
    },
);

function TableHead({
    className,
    variant,
    ...props
}: React.ComponentProps<'th'> & VariantProps<typeof tableHeadVariants>) {
    return (
        <th
            data-slot="table-head"
            className={cn(tableHeadVariants({ variant }), className)}
            {...props}
        />
    );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
    return (
        <td
            data-slot="table-cell"
            className={cn(
                'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                className,
            )}
            {...props}
        />
    );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
    return (
        <caption
            data-slot="table-caption"
            className={cn('text-muted-foreground mt-4 text-sm', className)}
            {...props}
        />
    );
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
